import {Button, MenuItem, useMediaQuery} from '@material-ui/core';
import {
  changePolicyFilter,
  changeSort,
  clearPolicies,
  clearSort,
  fetchPolicies,
  PolicyStatuses,
  PolicyTypes,
} from 'Agent/Policies/ducks/policy';
import PolicyRouteConstants from 'Agent/Policies/PolicyRouteConstants';
import {theme} from 'Core/components/WithTheme';
import {PAGE_ID, changeAnalyticsState} from 'Core/ducks/analytics';
import {ANALYTICS_EVENT_ACTION} from 'Core/components/Analytics/AnalyticsEventConstants';
import {ModalState, showModal} from 'Core/ducks/modal';
import * as O from 'fp-ts/lib/Option';
import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useHistory, useLocation} from 'react-router-dom';
import {ReducerState} from 'rootReducer';
import FileDownloadButton, {FileExtension} from 'Shared/components/FileDownloadButton';
import FileDownloadFab from 'Shared/components/FileDownloadFab';
import ActionsMenu from 'Shared/components/PagedView/ActionsMenu';
import Column from 'Shared/components/PagedView/Column';
import {
  withDateFilterProperties,
  withSelectFilterProperties,
  withStringFilterProperties,
} from 'Shared/components/PagedView/Filter';
import PagedView from 'Shared/components/PagedView/PagedView';
import RequireScope from 'Shared/components/RequireScope';
import {Scope} from 'Shared/constants/Scope';
import {getSourceOptions} from 'Shared/constants/Source';
import {AgencyFilters} from 'Shared/models/AgencyFilters';
import {addFilterParameters} from 'Shared/utilities/addFilterParameters';
import {DataSource, getEisFlagFromLinks} from 'Shared/utilities/DataSourceUtility';
import {toFormattedDateAsUTC} from 'Shared/utilities/DateFormatter';
import {toMoneyValue} from 'Shared/utilities/FormatMoney';
import {parseQueryString} from 'Shared/utilities/parsers';
import {maskPhoneNumber} from 'Shared/utilities/phoneNumberMasker';
import {prop} from 'Shared/utilities/prop';
import {withValueChangedHook} from 'Shared/utilities/withValueChanged';
import {selectUserScopes} from 'Users/selectors/userProfileSelectors';
import './PoliciesView.sass';
import {changeCorrespondenceFilter} from '../ducks/policyCorrespondence';

type PoliciesViewProps = {
  selectedAgency: AgencyFilters;
};

const PoliciesView = (props: PoliciesViewProps) => {
  const {policies, policyFilter, sort} = useSelector((state: ReducerState) => state.policy);
  const [policyFilter_holder,setPolicyFilter_holder] = useState({});
  const {selectedAgency} = props;
  const dispatch = useDispatch();
  const history = useHistory();
  const {search} = useLocation();
  const getQueryStringValue = parseQueryString(search);
  const accountNumber = O.getOrElse(() => policyFilter.accountNumber)(getQueryStringValue('accountNumber'));
  const insuredName = O.getOrElse(() => policyFilter.insuredName)(getQueryStringValue('insuredName'));
  const policyNumber = O.getOrElse(() => policyFilter.policyNumber)(getQueryStringValue('policyNumber'));
  const payorName = O.getOrElse(() => policyFilter.payorName)(getQueryStringValue('payorName'));
  const last4PayorNumber = O.getOrElse(() => policyFilter.last4PayorNumber)(getQueryStringValue('last4OfSSN'));
  const last4OfOwnerSSN = O.getOrElse(() => policyFilter.ownerLast4OfSSN)(getQueryStringValue('last4OfOwnerSSN'));
  const scopes = useSelector(selectUserScopes);
  const agentNumbers = useSelector((state: ReducerState) => state.user.profile.currentAgentNumbers);
  const viewPolicyDetail = (policyId: string, policyNum: string, source: string) =>
    history.push(PolicyRouteConstants.POLICY_DETAILS(policyId, policyNum, source));
  const {analytics} = useSelector((state: ReducerState) => state.analytics);
  const location = useLocation();

  useEffect(() => {
    dispatch(
      changePolicyFilter({
        ...policyFilter,
        accountNumber,
        insuredName,
        policyNumber,
        last4PayorNumber,
        payorName,
        ownerLast4OfSSN: last4OfOwnerSSN,
      })
    );
  }, [accountNumber, insuredName, policyNumber, payorName, last4PayorNumber, last4OfOwnerSSN]);

  useEffect(() => {
    return () => {
      dispatch(clearPolicies());
      dispatch(clearSort());
    };
  }, [clearPolicies, clearPolicies]);

  useEffect(() => {
    if((JSON.stringify(policyFilter) != JSON.stringify(policyFilter_holder)) || sort["value"]) {
      dispatch(
        fetchPolicies(
          agentNumbers,
          selectedAgency.isNewYork,
          selectedAgency.encryptedIdentifier,
          undefined,
          policyFilter,
          O.toUndefined(sort)
        )
      );
    }
    setPolicyFilter_holder(policyFilter);
  }, [policyFilter, fetchPolicies, selectedAgency, sort]);

  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const changeFilter = withValueChangedHook(policyFilter, changePolicyFilter, dispatch);
  const filterProperties = {
    changeFilter,
    filterData: policyFilter,
  };

  useEffect(() => {
    dispatch(changeAnalyticsState({page: {pageId: PAGE_ID[location.pathname]}}));
  }, []);

  // const formattedCsvDownloadSize = Number(parseInt(`${process.env.REACT_APP_POLICY_CSV_DOWNLOAD_SIZE}`)).toLocaleString()
  // const policyExtractToolTipMessage = `Only the first ${formattedCsvDownloadSize} records will be extracted`
  const policyExtractToolTipMessage = `Only the first 10,000 records will be extracted`;

  const exportToCsvProps = {
    startIcon: <i className="fa fa-2x fa-file-download" />,
    extension: FileExtension.CSV,
    downloadedFileName: 'FilteredPolicies',
    relativeFileLocation: `/api/policies/v1/csv${addFilterParameters({
      ...policyFilter,
      agentNumbers: agentNumbers.map(prop('value')),
      isNewYork: selectedAgency.isNewYork,
      encryptedIdentifier: selectedAgency.encryptedIdentifier,
    })}`,
    children: 'Export To CSV',
  };

  return (
    <PagedView
      pagedViewKey="policies"
      eventAction="agentPoliciesSettingFilter"
      sort={O.toUndefined(sort)}
      onChangeSort={sort => {
        dispatch(changeSort(sort));
      }}
      data={policies}
      actions={
        isMobile ? (
          <FileDownloadFab
            variant="extended"
            color="primary"
            displayTooltip
            tooltipMessage={policyExtractToolTipMessage}
            {...exportToCsvProps}
            aria-label="Export to CSV"
          />
        ) : (
          <FileDownloadButton
            variant="contained"
            color="primary"
            displayTooltip
            tooltipMessage={policyExtractToolTipMessage}
            {...exportToCsvProps}
            exportToCsvEvent="agentPolicies_exportToCsv"
            aria-label="Export to CSV"
          />
        )
      }
      loadMore={page =>
        dispatch(
          fetchPolicies(
            agentNumbers,
            selectedAgency.isNewYork,
            selectedAgency.encryptedIdentifier,
            page,
            policyFilter,
            O.toUndefined(sort)
          )
        )
      }
    >
      {columnProps => (
        <>
          <Column
            {...columnProps}
            title="Number"
            sortColumnKey="policyNumber"
            filterProperties={withStringFilterProperties(filterProperties, 'policyNumber')}
            default
            ddprivacy="mask"
          >
            {policy => policy.policyNumber}
          </Column>
          <Column
            {...columnProps}
            title="Account Number"
            sortColumnKey="accountNumber"
            filterProperties={withStringFilterProperties(filterProperties, 'accountNumber')}
            default
            ddprivacy="mask"
          >
            {policy => policy.accountNumber}
          </Column>
          <Column
            {...columnProps}
            title="Account Name"
            sortColumnKey="accountName"
            filterProperties={withStringFilterProperties(filterProperties, 'accountName')}
            default
            ddprivacy="mask"
          >
            {policy => policy.accountName}
          </Column>
          <Column
            {...columnProps}
            title="Insured Name"
            sortColumnKey="insuredName"
            filterProperties={withStringFilterProperties(filterProperties, 'insuredName')}
            default
            ddprivacy="mask"
          >
            {policy => policy.insuredName}
          </Column>
          <Column {...columnProps} title="Mode Premium" default>
            {policy => toMoneyValue(policy.premium)}
          </Column>
          <Column
            {...columnProps}
            title="Payor Name"
            sortColumnKey="payorName"
            filterProperties={withStringFilterProperties(filterProperties, 'payorName')}
            default
            ddprivacy="mask"
          >
            {policy => policy.payorName}
          </Column>
          <Column
            {...columnProps}
            title="Product Type"
            sortColumnKey="policyType"
            filterProperties={withSelectFilterProperties(filterProperties, 'policyType', PolicyTypes)}
            default
          >
            {policy => policy.policyType}
          </Column>
          <Column
            {...columnProps}
            title="Status"
            sortColumnKey="status"
            filterProperties={withSelectFilterProperties(filterProperties, 'status', PolicyStatuses)}
            default
          >
            {policy => policy.status!.charAt(0).toUpperCase() + policy.status!.slice(1)}
          </Column>
          <Column
            {...columnProps}
            title="Effective Date"
            sortColumnKey="effectiveDate"
            filterProperties={withDateFilterProperties(filterProperties, 'effectiveDate')}
            default
          >
            {policy => toFormattedDateAsUTC(policy.effectiveDate)}
          </Column>
          <Column
            {...columnProps}
            title="Source"
            sortColumnKey="source"
            filterProperties={withSelectFilterProperties(
              filterProperties,
              'sourceId',
              getSourceOptions(selectedAgency.isNewYork)
            )}
            default
          >
            {policy => policy.source!.name}
          </Column>
          <Column
            {...columnProps}
            title="Payor SSN"
            sortColumnKey="last4PayorNumber"
            filterProperties={withStringFilterProperties(filterProperties, 'last4PayorNumber')}
            ddprivacy="mask"
          >
            {policy => policy.last4PayorNumber}
          </Column>
          <Column
            {...columnProps}
            title="Issue Date"
            sortColumnKey="issueDate"
            filterProperties={withDateFilterProperties(filterProperties, 'issueDate')}
          >
            {policy => toFormattedDateAsUTC(policy.issueDate)}
          </Column>
          <Column {...columnProps} title="Owner Name" ddprivacy="mask">
            {policy => policy.ownerName}
          </Column>
          <Column {...columnProps} title="Owner Date of Birth" ddprivacy="mask">
            {policy => toFormattedDateAsUTC(policy.ownerDateOfBirth)}
          </Column>
          <Column {...columnProps} title="Owner Phone Number" ddprivacy="mask">
            {policy => maskPhoneNumber(policy.ownerPhoneNumber)}
          </Column>
          <Column
            {...columnProps}
            filterProperties={withStringFilterProperties(filterProperties, 'ownerLast4OfSSN')}
            title="Owner Last 4 of SSN"
            ddprivacy="mask"
          >
            {policy => policy.ownerLast4OfSSN}
          </Column>
          <Column {...columnProps} title="Owner Email" ddprivacy="mask">
            {policy => policy.ownerEmail}
          </Column>
          <Column {...columnProps} title="Owner Address" ddprivacy="mask">
            {policy => policy.ownerAddress}
          </Column>
          <Column {...columnProps} title="" isActions default>
            {policy => (
              <div>
                <RequireScope scopes={scopes} scope={Scope.ViewPolicy}>
                  <Button
                    className="primary-action-button"
                    id={'view-policy-details-button-' + policy.policyNumber}
                    onClick={() => {
                      dispatch(
                        changeAnalyticsState({
                          page: analytics.page,
                          ctaResponse: {eventAction: ANALYTICS_EVENT_ACTION.POLICIES_TILE_VIEWDETAILS},
                        })
                      );
                      viewPolicyDetail(policy.id!, policy.policyNumber!, getEisFlagFromLinks(policy.links));
                    }}
                    color="primary"
                    aria-label={`View Details of Policy Number ${policy.policyNumber}`}
                  >
                    View Details
                  </Button>
                </RequireScope>
                <ActionsMenu
                  id={`policy-actions-menu-${policy.id}`}
                  ariaLabel={`Actions Menu for Policy Number ${policy.policyNumber}`}
                >
                  {[
                    scopes.includes(Scope.SearchClaims) && (
                      <MenuItem
                        id={`policy-view-claims-btn-${policy.id}`}
                        key={`view-claims-${policy.policyNumber}`}
                        onClick={() => {
                          dispatch(
                            changeAnalyticsState({
                              page: analytics.page,
                              ctaResponse: {eventAction: ANALYTICS_EVENT_ACTION.POLICIES_TILE_CLAIMS},
                            })
                          );
                          const eisFlag = Number(getEisFlagFromLinks(policy.links)) === DataSource.EIS;
                          dispatch(
                            showModal(
                              ModalState.ClaimsConfirmation({policyNumber: policy.policyNumber!, eisFlag: eisFlag})
                            )
                          );
                        }}
                        aria-label="Claims"
                        role="menuitem"
                      >
                        Claims
                      </MenuItem>
                    ),
                    scopes.includes(Scope.SearchPolicyCorrespondence) && (
                      <MenuItem
                        id={`letters-view-corres-btn-${policy.id}`}
                        key={`view-corres-${policy.policyNumber}`}
                        onClick={() => {
                          dispatch(
                            changeCorrespondenceFilter({
                              accountNumber: '',
                              policyNumber: policy.policyNumber!,
                            })
                          );
                          history.push(PolicyRouteConstants.CORRESPONDENCE_SUMMARIES(policy.policyNumber!));
                        }}
                        aria-label="Letters"
                        role="menuitem"
                      >
                        View Correspondence
                      </MenuItem>
                    ),
                  ]}
                </ActionsMenu>
              </div>
            )}
          </Column>
        </>
      )}
    </PagedView>
  );
};

export default PoliciesView;
